import { Icon } from '@iconify/react';
import { Badge, Box, Checkbox, Chip, CircularProgress, Fade, IconButton, InputAdornment, ListItem, Popover, TextField, Typography } from '@mui/material'
import { t } from 'i18next';
import { debounce, isEmpty } from 'lodash';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react'
import { FixedSizeList, ListChildComponentProps } from 'react-window';


export type DataItem = {
    value: string | number;
    title: string;
};

type Props = {
    label: string;
    data?: DataItem[];
    checkedValues: (string | number)[];
    onChange: (items: (string | number)[]) => void;
    loading?: boolean;
    serverSearch?: boolean;
    onSearch?: (query: string) => void;

};



export const ListExpanderSimple: React.FC<Props> = ({
    label,
    data,
    checkedValues,
    onChange,
    loading,
    serverSearch = false,
    onSearch,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    // debounce search
    const debouncedSetSearchQuery = useMemo(
        () =>
            debounce((query: string) => {
                if (serverSearch) {
                    onSearch?.(query); // 🔹 ask parent for data
                } else {
                    setSearchQuery(query); // 🔹 local filtering
                }
            }, 300),
        [serverSearch, onSearch]
    );


    useEffect(() => {
        return () => {
            debouncedSetSearchQuery.cancel();
        };
    }, [debouncedSetSearchQuery]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        debouncedSetSearchQuery(value);
    };

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (loading) return;
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "custom-popover" : undefined;

    const filteredValues = useMemo(() => {
        if (!data || isEmpty(data)) return [];
        if (!searchQuery) return data;

        const lowerQuery = searchQuery.toLowerCase();
        return data.filter((item) => item.title.toLowerCase().includes(lowerQuery));
    }, [data, searchQuery]);

    const Row = ({ index, style }: ListChildComponentProps) => {
        const item = filteredValues[index];
        if (!item) return null;

        const isChecked = checkedValues.includes(item.value);

        const handleToggle = () => {
            const newChecked = isChecked
                ? checkedValues.filter((v) => v !== item.value)
                : [...checkedValues, item.value];

            onChange(newChecked);
        };

        return (
            <ListItem button dense style={style} key={item.value} onClick={handleToggle}>
                <Checkbox edge="start" checked={isChecked} tabIndex={-1} disableRipple />
                <Typography variant="body2">{item.title}</Typography>
            </ListItem>
        );
    };

    return (
        <>
            {/* Trigger Box */}
            <Box
                aria-describedby={id}
                onClick={handleClick}
                sx={{
                    cursor: loading ? "not-allowed" : "pointer",
                    borderRadius: 1,
                    width: '100%',
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: loading ? "hsla(0, 0%, 20%, 0.9)" : "hsla(0, 0%, 5%, .3)",
                    transition: "border 0.2s ease-in-out",
                    border: "2px solid transparent",
                    "&:hover": {
                        border: !loading ? '2px solid #A72C2C' : "2px solid transparent",
                    },
                    position: "relative",
                }}
            >
                <Badge
                    invisible={isEmpty(checkedValues)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    overlap="rectangular"
                    sx={{
                        position: "absolute",
                        top: -1,
                        right: -1,
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                    color="primary"
                    variant="dot"
                />
                <Typography
                    mr={3}
                    variant="subtitle2"
                >
                    {label}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    {!isEmpty(checkedValues) && (
                        <Chip
                            size="small"
                            label={checkedValues.length}
                            sx={{
                                backgroundColor: "rgba(33, 150, 243, 0.3)",
                                color: "#90caf9",
                                fontWeight: 500,
                            }}
                        />
                    )}
                    <Icon icon="bx:chevron-down" />
                </Box>
            </Box>

            {/* Popover List */}
            {open && (
                <Popover
                    id={id}
                    TransitionComponent={Fade}
                    transitionDuration={150}
                    PaperProps={{
                        sx: {
                            background: "hsla(0, 0%, 5%, .9)",
                            maxWidth: 300,
                            overflowY: "auto",
                            p: 0.5,
                            boxShadow: 3,
                            borderRadius: 1,
                            width: "auto",
                            minWidth: anchorEl ? anchorEl.offsetWidth : undefined,
                        },
                    }}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                    <Box sx={{ width: "100%" }}>
                        {/* Search */}
                        <Box p={1}>

                            <TextField
                                onChange={handleSearchChange}
                                value={searchInput}
                                size="small"
                                label={t('eap:table:filters:search')}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Icon icon="icons8:search" fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment:
                                        !isEmpty(searchQuery) && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSearchInput("");
                                                        setSearchQuery("");
                                                    }}
                                                >
                                                    <Icon icon="ic:round-clear" fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                }}
                            />
                        </Box>
                        {/* Virtualized List */}
                        {
                            loading ? <Box width={'100%'} height={'300px'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                                <CircularProgress size={20} />
                            </Box> : <FixedSizeList
                                height={300}
                                width="100%"
                                itemSize={50}
                                itemCount={filteredValues.length}
                            >
                                {Row}
                            </FixedSizeList>
                        }

                    </Box>
                </Popover>
            )}
        </>
    );
};

